import { FC, useEffect, useRef } from 'react';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import {
	ActionIcon,
	Drawer,
	Flex,
	Image,
	SimpleGrid,
	Text,
	Title,
	UnstyledButton,
} from '@mantine/core';
import { creatorActions } from '@/store/slices/creatorSlice';
import { AttributeValue } from '@/api/AttributeValueApi';
import { Preset } from '@/api/PresetApi';
import { Project } from '@/api/ProjectApi';
import { useAddCreatorProductModuleMutation } from '@/hooks/useAddCreatorProductModuleMutation';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import {
	checkProductModuleSwapMatching,
	checkProductModuleMatching,
	getFileSrc,
} from '@/utils/utilities';
import { IconChevronLeft } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import CreatorProductModuleApi, {
	CreatorProductModule,
} from '@/api/CreatorProductModuleApi';
import ProductModuleApi, { ProductModule } from '@/api/ProductModuleApi';
import Logo from '@/assets/images/logo.png';

const renderer = ProjectRenderer.getInstance();

export const ModulesListDrawer: FC = () => {
	const dispatch = useTypedDispatch();

	const { options, selectedProductModuleCollection, project, preset } =
		useTypedSelector((state) => state.creator);

	const isSwap = !!options.swapDetails.creatorProductModule;

	const titleRef = useRef<HTMLDivElement>(null);

	const addCreatorProductModuleMutation = useAddCreatorProductModuleMutation();

	const updateCreatorProductModuleMutation = useMutation({
		mutationFn: CreatorProductModuleApi.swapProductModule,
		onSuccess: (creatorProductModule) => {
			dispatch(
				creatorActions.handleCreatorProductModuleSwap({
					creatorProductModuleId: creatorProductModule.id,
					productModule: creatorProductModule.productModule,
				})
			);
		},
	});

	const productModulesQuery = useQuery({
		queryKey: [
			ProductModuleApi.queryKey,
			{
				sortBy: {
					key: 'listOrder',
					order: 'ASC',
				},
			},
			{
				'filter.productModuleCollection.id':
					selectedProductModuleCollection?.id,
			},
		],
		queryFn: ProductModuleApi.getAll,
		enabled: !!selectedProductModuleCollection,
	});

	const modules = options.isPresetMode
		? preset.creatorProductModules
		: project.creatorProductModules;

	const slotHostModule = modules.find(
		(creProdMod) =>
			creProdMod.id === options.selectedSlot?.creatorProductModuleId
	);

	const handleAddModule = (productModule: ProductModule) => () => {
		const payload: Partial<CreatorProductModule> = {
			listOrder: 100,
			productModule: { id: productModule.id } as ProductModule,
			selectedAttributeValues: slotHostModule
				? slotHostModule.selectedAttributeValues.map(
						(attributeValue) =>
							({
								id: attributeValue.id,
							}) as AttributeValue
					)
				: [],
		};

		if (options.isPresetMode) payload.preset = { id: preset.id } as Preset;
		else payload.project = { id: project.id } as Project;

		addCreatorProductModuleMutation.mutate(payload);
	};

	const handleSwapModule = (productModule: ProductModule) => () => {
		updateCreatorProductModuleMutation.mutate({
			id: options.swapDetails!.creatorProductModule!.id,
			productModule,
		});
	};

	const close = () => {
		renderer.deselectAll();
		dispatch(
			creatorActions.updateOptions({
				swapDetails: {
					slotTypes: [],
					creatorProductModule: null,
					modulesIds: [],
				},
			})
		);
	};

	useEffect(() => {
		if (!titleRef.current) return;
		titleRef.current.scrollIntoView();
	}, []);

	const isOpen = !!options.selectedSlot || isSwap;

	return (
		<Drawer
			opened={isOpen}
			onClose={close}
			title={
				<Flex justify="space-between">
					<Image src={Logo} w={200} className="creator-logo" />

					<ActionIcon onClick={close} variant="transparent">
						<IconChevronLeft />
					</ActionIcon>
				</Flex>
			}
		>
			<Title order={4} mt="md" mb={4}>
				Dostępne moduły
			</Title>
			<Text c="dimmed">Wybierz moduł, który chcesz dodać do projektu.</Text>
			<SimpleGrid
				py="md"
				cols={{
					base: 2,
					sm: 3,
				}}
				spacing="xs"
			>
				{productModulesQuery.data
					?.filter((prodMod) =>
						isSwap
							? checkProductModuleSwapMatching(
									prodMod,
									options.swapDetails!,
									project.creatorProductModules
								)
							: checkProductModuleMatching(
									prodMod,
									options.selectedSlot!,
									slotHostModule?.productModule
								)
					)
					.map((productModule) => {
						return (
							<UnstyledButton
								key={productModule.id}
								className="option-card"
								py="sm"
								px="xs"
								onClick={
									isSwap
										? handleSwapModule(productModule)
										: handleAddModule(productModule)
								}
							>
								<Image
									src={getFileSrc(productModule.image?.thumbnails[200])}
									w={75}
									h={75}
									mb="sm"
									loading="lazy"
								/>
								<Text size="xs" fw="bold">
									{productModule.name}
								</Text>
							</UnstyledButton>
						);
					})}
			</SimpleGrid>
		</Drawer>
	);
};
